// Hand-authored additions on top of the generated files: browser-facing WS topics that the gateway /
// agent add beyond the device->cloud schema, and the binary frame kinds. Not generated (no schema yet).
import type { DeviceTopicPayloads } from './topics';

export interface PresencePayload {
  online: boolean;
  session_id: string | null;
  last_seen: number | null;
  gw_id?: string;
}
/** `levels` is a device->cloud topic now (generated `Levels`); kept as an alias for the hooks. */
export type LevelsPayload = DeviceTopicPayloads['levels'];
export interface LogLinePayload {
  ts: number;
  level: 'DEBUG' | 'INFO' | 'WARNING' | 'ERROR' | string;
  src: string;
  msg: string;
}
export interface TimecodePayload {
  tc: string;
  source: string;
  framerate: number;
  locked: boolean;
  drift_ppm: number;
}

/** Everything a browser can subscribe to on /ws (local) or /gw/browser (cloud). */
export interface TopicPayloads extends DeviceTopicPayloads {
  presence: PresencePayload;
  logs: LogLinePayload;
  timecode: TimecodePayload;
  /** Binary topics: JSON side only announces start/stop; frames arrive as binary (see BINARY_KIND). */
  preview: { output: string; fps: number };
  audio_opus: { sample_rate: 48000; channels: 1; frame_ms: 20 };
}
export type Topic = keyof TopicPayloads;
export const ALL_TOPICS: readonly Topic[] = [
  'snapshot',
  'telemetry',
  'health',
  'caption_event',
  'cmd_result',
  'alert',
  'file_sync',
  'update_status',
  'settings_changed',
  'presence',
  'levels',
  'logs',
  'timecode',
  'preview',
  'audio_opus',
] as const;

/**
 * Binary frames on the browser sockets. First byte = kind.
 *   0x01 audio:   [0x01][u16 seq][u32 ts_ms][opus]            (PLAN: live audio listen)
 *   0x02 preview: [0x02][u8 output index][jpeg]
 *   0x10 cloud wrapper: [0x10][16-byte device uuid][inner frame]
 */
export const BINARY_KIND = { AUDIO_OPUS: 0x01, PREVIEW_JPEG: 0x02, DEVICE_WRAPPED: 0x10 } as const;

// ── Device name -> hotspot SSID (mirror of glyph_protocol.settings.ssid_slug / derive_ap_ssid) ──
/** 32-byte SSID minus "Glyph-" and "-XXXX". */
export const SSID_SLUG_MAX = 21;
export const DEVICE_NAME_MAX = 32;
/**
 * The part of `Glyph-<slug>-<serial4>` that comes from the free-form device name: runs of anything
 * but letters, digits and hyphens become one hyphen ("Stage Left 1" -> "Stage-Left-1"), trimmed to
 * 21 characters; an empty result falls back to `fallback`.
 */
export function ssidSlug(name: string, fallback = 'G1'): string {
  const slug = (name || '')
    .replace(/[^A-Za-z0-9-]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, SSID_SLUG_MAX)
    .replace(/-+$/g, '');
  return slug || fallback;
}
/** `Glyph-<slug>-<serial4>`; `suffix` "auto" = last four of the serial (masked as "····" when unknown). */
export function deriveApSsid(name: string, suffix: string, serial4 = '····'): string {
  const sfx = suffix === 'auto' ? serial4 : suffix;
  const tail = sfx ? `-${sfx}` : '';
  return `Glyph-${ssidSlug(name).slice(0, 32 - 'Glyph-'.length - tail.length)}${tail}`;
}

/** The FDG Admin account's fixed username (protocol settings.py ADMIN_USERNAME); the tech account's is a setting. */
export const ADMIN_USERNAME = 'fdgadmin';

/** Update lock switched off fleet-wide (2026-10-21): the consoles hide the lock controls and never treat a unit as
 *  locked; the cloud unlocks units that still report locked. Flip with GLYPH_UPDATE_LOCK_ENABLED on the cloud. */
export const UPDATE_LOCK_ENABLED = false;
